import React from 'react';
import { Alert, Card, Col, DatePicker, Form, Grid, Row, theme } from 'antd';
import {
  ArrowDownOutlined,
  ArrowRightOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import TransportFormSectionTitle from './TransportFormSectionTitle';
import TransportAddressListField from './TransportAddressListField';
import { transportDatePickerProps } from './transportFormUtils';

interface TransportRouteSectionProps {
  onLoadingTouched: () => void;
  onDeliveryTouched: () => void;
}

const TransportRouteSection: React.FC<TransportRouteSectionProps> = ({
  onLoadingTouched,
  onDeliveryTouched,
}) => {
  const { t } = useTranslation(['transport']);
  const { token } = theme.useToken();
  const breakpoints = Grid.useBreakpoint();
  const status = Form.useWatch('status');
  const actualDeliveryRequired = status === 'DELIVERED';

  return (
    <Card title={<TransportFormSectionTitle step={2} icon={<EnvironmentOutlined />} label={t('transport:sections.routeSchedule')} />} style={{ marginTop: 24 }}>
      <Row gutter={[20, 20]} align="top">
        <Col xs={24} xl={11}>
          <Form.Item
            name="loadingDate"
            label={t('transport:fields.loadingDate')}
            rules={[{ required: true, message: t('transport:validation.loadingDateRequired') }]}
          >
            <DatePicker
              placeholder={t('transport:placeholders.selectDate')}
              {...transportDatePickerProps}
            />
          </Form.Item>

          {/* Any interaction with the loading list (typing in a textarea or
              clicking add/remove) marks it user-owned, so the create-mode
              prefill from selected orders stops overwriting it. */}
          <div
            onInput={onLoadingTouched}
            onClickCapture={onLoadingTouched}
          >
            <TransportAddressListField
              name="loadingAddresses"
              variant="loading"
              label={t('transport:fields.loadingAddresses')}
              placeholder={t('transport:placeholders.addressLine')}
              errorMessage={t('transport:validation.loadingAddressRequired')}
            />
          </div>
        </Col>

        {/* Purely decorative from→to cue between the two address columns —
            a right-pointing arrow on desktop, a down-pointing arrow when the
            columns stack on narrow widths (antd's own breakpoint hook, no
            custom CSS). No form state involved. */}
        <Col xs={24} xl={2} style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ color: token.colorTextTertiary, fontSize: 20, padding: '8px 0' }}>
            {breakpoints.xl ? <ArrowRightOutlined /> : <ArrowDownOutlined />}
          </div>
        </Col>

        <Col xs={24} xl={11}>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="estimatedDelivery"
                label={t('transport:fields.estimatedDelivery')}
                rules={[{ required: true, message: t('transport:validation.estimatedDeliveryRequired') }]}
              >
                {/* Delivery dates are logistics milestones — date-only is the
                    working granularity; hours proved distracting in the form UX review. */}
                <DatePicker
                  placeholder={t('transport:placeholders.selectDate')}
                  {...transportDatePickerProps}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="actualDelivery"
                label={t('transport:fields.actualDelivery')}
                rules={actualDeliveryRequired
                  ? [{ required: true, message: t('transport:validation.actualDeliveryRequired') }]
                  : []}
              >
                <DatePicker
                  placeholder={t('transport:placeholders.selectDate')}
                  {...transportDatePickerProps}
                />
              </Form.Item>
              {actualDeliveryRequired ? (
                <Alert
                  type="info"
                  showIcon
                  message={t('transport:actualDeliveryRequiredHint')}
                  style={{ marginTop: -12, marginBottom: 12 }}
                />
              ) : null}
            </Col>
          </Row>

          {/* Any interaction with the delivery list (typing in a textarea or
              clicking add/remove) marks it user-owned, so the create-mode
              prefill from selected orders stops overwriting it. */}
          <div
            onInput={onDeliveryTouched}
            onClickCapture={onDeliveryTouched}
          >
            <TransportAddressListField
              name="deliveryAddresses"
              variant="delivery"
              label={t('transport:fields.deliveryAddresses')}
              placeholder={t('transport:placeholders.addressLine')}
              errorMessage={t('transport:validation.deliveryAddressRequired')}
            />
          </div>
        </Col>
      </Row>
    </Card>
  );
};

export default TransportRouteSection;
